import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * O núcleo do Radar (transversal/at-82, ADR 0046) — a API compartilhada pelos radares.
 *
 * Substitui as duas rotas de candidatos que existiam por cópia (`/contac-recomendacoes/candidatos`
 * e `/contac-recomendacoes/relacionamento/candidatos`): hoje a seção "achados do assistente" de
 * QUALQUER radar sai daqui, com `radarId` na URL.
 *
 * ⚠️ **Nenhum `catch` neste arquivo** (regra da at-60), e aqui a consequência é a mais cara do
 * sistema: "o assistente não propôs achados hoje" é um desfecho LEGÍTIMO e frequente. Um catch
 * silencioso faria falha de rede ter exatamente a silhueta de "está tudo bem" — numa tela de
 * diretoria e numa fila de cobrança. Quem trata o erro é o componente, que distingue os cinco
 * estados de `exploracao.estado`.
 */

export type FormatoDoNumero = 'numero' | 'moeda' | 'percentual';

export type VotoValor = 'UP' | 'DOWN';

/**
 * Os motivos do voto, separados por qual voto aceitam.
 *
 * ⚠️ A separação é imposta pelo BACKEND (`400` na combinação inversa), não só pela tela: o motivo
 * entra no prompt da próxima exploração e na administração como leitura do que a equipe pensou —
 * "Já agi com base nisso" num voto negativo inverteria o sentido do registro.
 */
export type VotoMotivo =
  | 'UTIL_VOU_VERIFICAR'
  | 'UTIL_JA_AGI'
  | 'JA_SABIA'
  | 'NUMERO_NAO_BATE'
  | 'IRRELEVANTE';

export interface MeuVoto {
  voto: VotoValor;
  motivo: VotoMotivo | null;
}

export interface ProvenienciaDoRadar {
  /** Tabelas e views consultadas, com o nome real. */
  fontes: string[];
  /** A regra da consulta, com os parâmetros por extenso. */
  regra: string;
  /** O que o número não diz. */
  limitacoes: string[];
}

export interface ColunaDoAchado {
  chave: string;
  rotulo: string;
  tipo: 'texto' | 'numero' | 'moeda' | 'percentual' | 'competencia' | 'placa';
}

/**
 * Um achado do assistente.
 *
 * ⚠️ `numero` é lido pelo SERVIDOR do resultado da consulta apontada, nunca do texto do modelo —
 * é a invariante 1 do padrão. `linhas` já vem na resposta (o "Ver linhas" não faz nova chamada).
 */
export interface AchadoDoAssistente {
  id: string;
  ordem: number;
  titulo: string;
  texto: string;
  numero: number;
  formato: FormatoDoNumero;
  unidade: string;
  /**
   * `ACOMPANHAMENTO` é a re-execução diária, SEM modelo, de um recorte que a equipe aprovou pelo
   * voto; `DESCOBERTA` é a proposta nova do dia.
   *
   * ⚠️ Opcional no tipo de propósito: durante a janela de deploy o backend antigo não devolve o
   * campo, e a ausência precisa significar "candidato", nunca quebrar a seção.
   */
  origem?: 'DESCOBERTA' | 'ACOMPANHAMENTO';
  proveniencia: ProvenienciaDoRadar;
  colunas: ColunaDoAchado[];
  linhas: Record<string, string | number | null>[];
  /** O recorte listável ("de quais associados este número fala"). Ausente = sem lista a abrir. */
  recorte: Record<string, string> | null;
  /** Em quantos dias distintos este mesmo recorte já apareceu. */
  aparicoes: number;
  /**
   * Só o voto DO REQUERENTE. A contagem de votos vive apenas na administração — exibi-la ao lado
   * do botão enviesaria o voto seguinte (decisão de 06/09/2026).
   */
  meuVoto: MeuVoto | null;
}

/**
 * Os cinco estados da exploração, que a tela **não pode colapsar**.
 *
 * `SEM_PROPOSTA` (o assistente olhou e não propôs nada) e `FALHOU` (a exploração quebrou) têm a
 * mesma silhueta se tratados como "lista vazia", e levam a conclusões opostas.
 */
export type EstadoDaExploracao =
  | 'CONCLUIDA'
  | 'SEM_PROPOSTA'
  | 'FALHOU'
  | 'EM_ANDAMENTO'
  | 'NAO_EXECUTADA'
  /**
   * at-83: o ADMIN (ou o TI) desligou este radar na Administração do Sistema.
   *
   * 🔴 Não é `NAO_EXECUTADA` e não é `FALHOU`. Os três mandam o usuário a lugares diferentes:
   * "não configurado neste ambiente" e "quebrou" mandam acionar a TI; "desativado" manda falar
   * com a administração — e ninguém precisa investigar nada.
   */
  | 'DESATIVADO';

export interface AchadosDoAssistenteResponse {
  radarId: string;
  rotulo: string;
  /** false = a IA não está configurada neste ambiente. */
  disponivel: boolean;
  /** DD/MM/AAAA — o conjunto de achados é do dia, e o mesmo para todos. */
  diaReferencia: string;
  janela: { descricao: string; chave: string };
  exploracao: {
    estado: EstadoDaExploracao;
    disparadoPor: 'CRON' | 'DEMANDA' | null;
    concluidoEm: string | null;
    aviso: string | null;
    /** Propostas cujo apontamento não resolveu. Corte nunca silencioso. */
    candidatosDescartados: number;
  };
  /**
   * at-83: preenchido só quando `estado === 'DESATIVADO'` — quem desligou e quando.
   *
   * É o que permite à tela dizer a quem cobrar a religação, em vez de um aviso anônimo.
   */
  desativacao: { por: string | null; em: string | null } | null;
  achados: AchadoDoAssistente[];
}

export async function buscarAchadosDoAssistente(
  radarId: string,
): Promise<AchadosDoAssistenteResponse> {
  const { data } = await axios.get<AchadosDoAssistenteResponse>(
    `${BACKEND_URL}/radar/${encodeURIComponent(radarId)}/achados-do-assistente`,
    { headers: authHeader() },
  );
  return data;
}

/**
 * Vota num achado.
 *
 * ⚠️ É **`POST`**, não `PUT`: a tabela é append-only e cada clique grava um EVENTO — trocar de UP
 * para DOWN é outro POST, e o vigente é o último. É o que sustenta a trilha "quem votou e quando"
 * que a administração mostra.
 */
export async function votarNoAchado(
  radarId: string,
  achadoId: string,
  voto: { voto: VotoValor; motivo?: VotoMotivo; texto?: string },
): Promise<{ meuVoto: MeuVoto }> {
  const { data } = await axios.post<{ meuVoto: MeuVoto }>(
    `${BACKEND_URL}/radar/${encodeURIComponent(radarId)}/achados/${encodeURIComponent(achadoId)}/voto`,
    voto,
    { headers: authHeader() },
  );
  return data;
}

/** Grava o evento `RETIRADO` — não apaga o voto anterior. Idempotente no backend. */
export async function retirarVotoDoAchado(
  radarId: string,
  achadoId: string,
): Promise<{ meuVoto: null }> {
  const { data } = await axios.delete<{ meuVoto: null }>(
    `${BACKEND_URL}/radar/${encodeURIComponent(radarId)}/achados/${encodeURIComponent(achadoId)}/voto`,
    { headers: authHeader() },
  );
  return data;
}

// ---------------------------------------------------------------------------------------------
// Administração (`/admin/sistema/radar`, ADMIN)
//
// É aqui — e só aqui — que as CONTAGENS de voto aparecem.
// ---------------------------------------------------------------------------------------------

export type EstadoDaAvaliacao = 'NEUTRO' | 'APROVADO' | 'REPROVADO';

/**
 * Os limiares que decidem o estado, vindos da RESPOSTA e nunca escritos na tela.
 *
 * ⚠️ Copiá-los para o texto do drawer faria a tela descrever uma régua que o sistema deixou de
 * aplicar no primeiro ajuste — a mesma armadilha da tela de configuração do Contac.
 */
export interface LimiaresDaAvaliacao {
  minimoDeVotos: number;
  proporcaoDecisiva: number;
  minimoDeAssinaturasPorTema: number;
  diasDeAvaliacao: number;
}

export interface AssinaturaAvaliada {
  assinatura: string;
  ferramenta: string;
  parametros: Record<string, unknown>;
  campoDoNumero: string;
  rotuloDaLinha: string | null;
  ultimoTitulo: string;
  estado: EstadoDaAvaliacao;
  votosUp: number;
  votosDown: number;
  aparicoes: number;
  primeiraAparicao: string;
  ultimaAparicao: string;
}

export interface TemaAvaliado {
  nivel: string;
  chave: string;
  descricao: string;
  estado: EstadoDaAvaliacao;
  votosUp: number;
  votosDown: number;
  assinaturas: number;
  /** true = já vale como instrução dura no prompt (atingiu o mínimo de assinaturas distintas). */
  duro: boolean;
}

export interface AchadoAvaliado {
  id: string;
  dia: string;
  titulo: string;
  assinatura: string;
  origem: string;
  /** O assistente quis propor isto de novo e a supressão o barrou. Só aparece na administração. */
  suprimido: boolean;
  up: number;
  down: number;
  motivos: Record<string, number>;
  /** O que a equipe escreveu no voto. Entra no prompt da próxima exploração, delimitado. */
  textos: string[];
}

export interface RadarAvaliado {
  radarId: string;
  rotulo: string;
  /** false = há dados de um radar que não está mais registrado no catálogo. */
  registrado: boolean;
  assinaturas: AssinaturaAvaliada[];
  temas: TemaAvaliado[];
  achados: AchadoAvaliado[];
  porFerramenta: { ferramenta: string; achados: number; up: number; down: number }[];
}

export interface AvaliacaoDoRadarResponse {
  dias: number;
  limiares: LimiaresDaAvaliacao;
  radares: RadarAvaliado[];
}

export async function buscarAvaliacaoDoRadar(
  radarId?: string,
  dias?: number,
): Promise<AvaliacaoDoRadarResponse> {
  const { data } = await axios.get<AvaliacaoDoRadarResponse>(`${BACKEND_URL}/radar/admin/avaliacao`, {
    headers: authHeader(),
    params: {
      ...(radarId ? { radarId } : {}),
      ...(dias ? { dias: String(dias) } : {}),
    },
  });
  return data;
}

export interface ExploracaoRegistrada {
  id: string;
  radarId: string;
  dia: string;
  chaveDaJanela: string;
  status: 'EM_ANDAMENTO' | 'CONCLUIDA' | 'SEM_PROPOSTA' | 'FALHOU';
  disparadoPor: 'CRON' | 'DEMANDA';
  candidatosDescartados: number;
  aviso: string | null;
  modelo: string | null;
  idasAoModelo: number;
  tokensEntrada: number;
  tokensSaida: number;
  iniciadoEm: string;
  concluidoEm: string | null;
}

export interface ExploracoesDoRadarResponse {
  exploracoes: ExploracaoRegistrada[];
}

export async function buscarExploracoesDoRadar(
  radarId?: string,
  limite?: number,
): Promise<ExploracoesDoRadarResponse> {
  const { data } = await axios.get<ExploracoesDoRadarResponse>(
    `${BACKEND_URL}/radar/admin/exploracoes`,
    {
      headers: authHeader(),
      params: {
        ...(radarId ? { radarId } : {}),
        ...(limite ? { limite: String(limite) } : {}),
      },
    },
  );
  return data;
}

/**
 * O interruptor de cada radar (transversal/at-83, ADR 0052).
 *
 * ⚠️ Sem `catch`, como todo o resto deste arquivo: "o radar está desligado" e "não consegui
 * perguntar" levam a ações opostas, e quem distingue é a tela.
 */
export interface AtivacaoDoRadar {
  radarId: string;
  rotulo: string;
  ativo: boolean;
  /** Quem ligou ou desligou por último. `null` enquanto ninguém tocou no interruptor. */
  alteradoPor: { id: string; nome: string } | null;
  alteradoEm: string | null;
}

export async function buscarAtivacaoDosRadares(): Promise<AtivacaoDoRadar[]> {
  const { data } = await axios.get<AtivacaoDoRadar[]>(`${BACKEND_URL}/radar/admin/ativacao`, {
    headers: authHeader(),
  });
  return data;
}

export async function definirAtivacaoDoRadar(
  radarId: string,
  ativo: boolean,
): Promise<AtivacaoDoRadar> {
  const { data } = await axios.put<AtivacaoDoRadar>(
    `${BACKEND_URL}/radar/admin/ativacao/${encodeURIComponent(radarId)}`,
    { ativo },
    { headers: authHeader() },
  );
  return data;
}
